import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  SelectChangeEvent,
  styled,
  TextField,
} from '@mui/material';
import { useAppSelector } from 'setup/store';
import { useStoreActions } from 'actions/store-actions';
import { useSessionActions } from 'actions/session-actions';
import { IDateFilter } from 'common/interfaces';
import { DATE_FORMATS } from 'common/constants';
import { DateRangePicker, Range, RangeKeyDict } from 'react-date-range';
//@ts-ignore
import 'react-date-range/dist/styles.css'; // main style file
//@ts-ignore
import 'react-date-range/dist/theme/default.css'; // theme css file
import { enUS } from 'date-fns/locale';
import { dateFormatterWithoutTime, formatDateTime } from 'common/utils/date-util';
import { IStore } from 'common/interfaces/istore';
import UILogger from 'common/utils/logger';
import classes from './ReportSearchFilter.module.scss';
import { useDayReportEntryActions } from 'actions/day-report-entry-actions';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { toNumber } from 'lodash';

type ReportSearchFilterProps = {
  currentDate?: Date | undefined;
  duration?: number;
  handleSubmit: (dateFilter: IDateFilter, store: IStore | undefined | null) => void;
};
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.primary.main, // Normal border color
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.dark, // On hover
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main, // On focus
      borderWidth: '2px',
    },
  },
}));

const ReportSearchFilter: FC<ReportSearchFilterProps> = ({ duration = 7, currentDate, handleSubmit }) => {
  const { t } = useTranslation();
  const appConfig = useAppSelector((state) => state.appReducer.appConfig);
  const stores = useAppSelector((store) => store.storeReducer.stores);
  const selectedStore = useAppSelector((store) => store.sessionReducer.selectedStore);
  const loading = useAppSelector((store) => store.storeReducer.loading);
  const [_stores, setStores] = useState<readonly IStore[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const paramStartDate = searchParams.get('sd');
  const paramEnDate = searchParams.get('ed');
  const paramStoreId = searchParams.get('s');
  const propDate = moment(currentDate || new Date());
  console.log('propDate::', propDate);

  const [_selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [dateFilter, setDateFilter] = useState<IDateFilter>({
    startDate: paramStartDate
      ? paramStartDate
      : formatDateTime(
          moment(propDate)
            .subtract(duration - 1, 'day')
            .toDate(),
          DATE_FORMATS.YYYYMMDD,
        ) || '2025-04-01',
    endDate: paramEnDate ? paramEnDate : formatDateTime(propDate.toDate(), DATE_FORMATS.YYYYMMDD) || '2025-04-01',
  });
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const refLastKey = useRef<string>('-1');
  const pickerRef = useRef<HTMLDivElement>(null);

  const [selectedRange, setRange] = useState<Range>({
    startDate: paramStartDate
      ? moment(paramStartDate, DATE_FORMATS.YYYYMMDD).toDate()
      : moment(propDate)
          .subtract(duration - 1, 'day')
          .toDate(),
    endDate: paramEnDate ? moment(paramEnDate, DATE_FORMATS.YYYYMMDD).toDate() : propDate.toDate(),
    key: 'selection',
    showDateDisplay: true,
  });
  const [inputValue, setInputValue] = useState<string>('All');

  const { loadAllStores, loadStoreById, searchStores } = useStoreActions();
  const { setSelectedStore } = useSessionActions();
  const { resetContext } = useDayReportEntryActions();

  const _showCalendar = () => {
    setShowCalendar(!showCalendar);
  };

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    }

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  const init = async () => {
    resetContext();
    if (paramStoreId) {
      const storeIdNum = toNumber(paramStoreId);
      let fs = undefined;
      if (stores?.items) {
        fs = stores.items.find((st) => st.id == storeIdNum);
      }
      if (fs) {
        setSelectedStore(fs);
      } else {
        fs = await loadStoreById(storeIdNum);
        if (fs) {
          setSelectedStore(fs);
        }
      }
      _handleSubmit(fs, dateFilter);
    } else {
      _handleSubmit(undefined, dateFilter);
    }
    await loadAllStores(1, appConfig.defaultListSize);
  };

  useEffect(() => {
    init();
  }, []);

  const _searchStores = async (inputValue: string) => {
    if (refLastKey.current !== inputValue) {
      const data = await searchStores({ term: inputValue }, 1, 10);
      setStores(data?.items || []);
      refLastKey.current = inputValue;
    }
  };

  const handleStoreChange = (event: SelectChangeEvent) => {
    console.log('>>>>', event.target.value);
    const store = stores?.items?.find((store) => store.id === Number(event.target.value));
    setSelectedStore(store);
  };

  const onDateRangeChange = (rangesByKey: RangeKeyDict) => {
    console.log('rangesByKey::', rangesByKey);
    setRange(rangesByKey.selection);
    const startDate = formatDateTime(rangesByKey.selection.startDate, DATE_FORMATS.YYYYMMDD);
    const endDate = formatDateTime(rangesByKey.selection.endDate, DATE_FORMATS.YYYYMMDD);
    console.log('startDate::', startDate, endDate);
    if (startDate && endDate) {
      setDateFilter({
        startDate,
        endDate,
      });
    }
    // setShowCalendar(false);
  };

  const _handleSubmit = (store?: IStore, df?: IDateFilter) => {
    if (handleSubmit && (df || dateFilter)) {
      setSearchParams({
        s: ((store || selectedStore)?.id || '').toString(),
        sd: (df || dateFilter).startDate,
        ed: (df || dateFilter).endDate,
      });
      handleSubmit(df || dateFilter, store || selectedStore);
      setShowCalendar(false);
    } else {
      UILogger.log('invalid form data', df || dateFilter, selectedStore);
    }
  };
  useEffect(() => {
    if (inputValue) {
      _searchStores(inputValue);
    }
  }, [inputValue]);

  return (
    <Box>
      <FormControl style={{ width: '20%' }}>
        <Autocomplete
          id="AutoCompleteReportSearch"
          options={_stores}
          onOpen={() => _searchStores('')}
          getOptionLabel={(option) => {
            return !option.storeNumber || option.storeNumber == -1
              ? 'All'
              : '#' + option.storeNumber + ' ' + option.storeName;
          }}
          inputValue={inputValue}
          value={
            selectedStore ||
            ({
              id: -1,
              storeNumber: -1,
              storeName: '',
              zipcode: '',
              email: '',
              phoneNo: '',
              createdTime: '',
              addressCity: '',
              addressState: '',
              addressStreet: '',
              brandId: -1,
              displayName: '',
              status: '',
              deletedTime: '',
              enterpriseId: -1,
              storeAdminId: -1,
              franchiseeAdminId: -1,
              updatedTime: '',
              franchiseeAdminName: '',
            } as IStore)
          }
          onInputChange={(event, newInputValue, reason) => {
            console.log('onInputChange:', newInputValue, reason);
            if (reason !== 'reset') {
              setInputValue(newInputValue);
            }
          }}
          loading={loading}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                {/* {' #' + option.storeNumber + ' ' + option.storeName + ' zip: ' + option.zipcode} */}
                <span className={classes.storeOptionLabel}>{'#'}</span>
                <span className={classes.storeOptionText}>
                  {option.storeNumber === -1 ? 'All' : option.storeNumber}
                </span>
                <span className={classes.storeOptionLabel}>
                  {option.storeNumber > -1 ? ' ' + option.storeName : ''}
                </span>
              </li>
            );
          }}
          // fullWidth
          // style={{ height: '10px' }}
          // style={{ border: '1px solid blueborder: 1px solid rgba(25, 118, 210, 0.5);', borderRadius: '4px' }}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              // style={{ height: '10px' }}
              // label="Stores"
              placeholder="Search Stores"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          onChange={(event, newValue) => {
            setSelectedStore(newValue || undefined);
            // console.log('>>>>>>', newValue);
          }}
        />
      </FormControl>
      <FormControl style={{ width: '40%', marginLeft: '20px' }}>
        <Button variant="outlined" title="Date" onClick={_showCalendar} size="large" style={{ height: '55px' }}>
          {' '}
          {selectedRange?.startDate && selectedRange?.endDate
            ? '' +
              dateFormatterWithoutTime(selectedRange.startDate) +
              ' - ' +
              dateFormatterWithoutTime(selectedRange.endDate)
            : null}
        </Button>
        {selectedRange && showCalendar ? (
          <Box
            sx={{
              right: 0,
            }}
            className={classes.datePicker}
            ref={pickerRef}
          >
            <DateRangePicker
              locale={enUS}
              ranges={[selectedRange]}
              onChange={onDateRangeChange}
              editableDateInputs
              moveRangeOnFirstSelection={true}
              retainEndDateOnFirstSelection
            />
          </Box>
        ) : null}
      </FormControl>
      <FormControl style={{ width: '10%', marginLeft: '20px' }}>
        <Button
          variant="contained"
          title="Date"
          onClick={() => _handleSubmit()}
          size="large"
          style={{ height: '55px' }}
        >
          Submit
        </Button>
      </FormControl>
    </Box>
  );
};

export default ReportSearchFilter;
